import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Heart, Menu, X, Truck } from "lucide-react";
import { api, money, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const NAV_LINKS = [
  { to: "/shop", label: "Shop" },
  { to: "/categories", label: "Categories" },
  { to: "/gallery", label: "Gallery" },
  { to: "/deals", label: "Deals" },
  { to: "/new-arrivals", label: "New Arrivals" },
  { to: "/clearance", label: "Clearance" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/pickup", label: "Pickup", accent: true },
];

export const Header = () => {
  const { user, login, logout } = useAuth();
  const { totalCount } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const timer = useRef(null);
  const navigate = useNavigate();
  const [logo, setLogo] = useState("/images/logo.png");

  useEffect(() => { api.get("/settings").then((r) => { if (r.data.logo_url) setLogo(r.data.logo_url); }).catch(() => {}); }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!query || query.length < 2) { setSuggestions([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get("/products/search-suggestions", { params: { q: query } });
        setSuggestions(r.data);
      } catch (e) { console.error("Search suggestions failed", e); }
    }, 200);
  }, [query]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setShowSuggest(false);
    navigate(`/shop?q=${encodeURIComponent(query.trim())}`);
  };

  return (
    <>
      <div className="bg-brand-navy text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 font-semibold">
            <span className="hidden sm:inline">NOW OPEN</span>
            <span className="sm:hidden">NOW OPEN</span>
            <span className="text-brand-yellow">·</span>
            <span className="hidden sm:inline">Barrhaven · Ottawa</span>
          </div>
          <div className="flex items-center gap-1 text-brand-yellow font-bold">
            <Truck className="h-3.5 w-3.5" />
            <span>Drive-Through Pickup</span>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 h-16">
            <Link to="/" data-testid="header-logo" className="flex items-center shrink-0">
              <img src={mediaUrl(logo)} alt="Barrhaven Dealswagon Outlet" className="h-12 sm:h-14 w-auto" />
            </Link>

            <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-xl relative">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  data-testid="header-search-input"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setShowSuggest(true); }}
                  onFocus={() => setShowSuggest(true)}
                  onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
                  placeholder="Search deals, brands, categories..."
                  className="w-full pl-10 pr-4 h-10 rounded-full border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy focus:bg-white"
                />
                {showSuggest && suggestions.length > 0 && (
                  <div className="absolute top-11 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-2xl p-2 z-50">
                    {suggestions.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        data-testid={`search-suggest-${s.id}`}
                        onMouseDown={() => { navigate(`/product/${s.slug}`); setQuery(""); }}
                        className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-slate-50 text-left"
                      >
                        <img src={s.featured_image} alt="" className="h-10 w-10 rounded object-cover" />
                        <div className="flex-1 text-sm text-slate-800 line-clamp-1">{s.product_name}</div>
                        <div className="text-sm font-bold text-brand-navy">{money(s.price)}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </form>

            <div className="flex items-center gap-1 ml-auto">
              <Link to="/account" data-testid="header-account-link"
                className="hidden sm:flex items-center gap-1.5 text-sm text-slate-700 hover:text-brand-navy px-2 py-2">
                <User className="h-5 w-5" />
                <span className="hidden lg:inline font-semibold">
                  {user ? (user.name?.split(" ")[0] || "Account") : "Sign in"}
                </span>
              </Link>
              <Link to="/wishlist" data-testid="header-wishlist-link"
                className="hidden sm:flex items-center text-slate-700 hover:text-brand-red p-2">
                <Heart className="h-5 w-5" />
              </Link>
              <Link to="/cart" data-testid="header-cart-link"
                className="relative flex items-center text-slate-700 hover:text-brand-navy p-2">
                <ShoppingCart className="h-5 w-5" />
                {totalCount > 0 && (
                  <span data-testid="header-cart-count" className="absolute -top-0.5 -right-0.5 bg-brand-red text-white rounded-full text-[10px] font-black h-5 min-w-[1.25rem] px-1 flex items-center justify-center">
                    {totalCount}
                  </span>
                )}
              </Link>
              <button data-testid="header-mobile-menu-btn"
                onClick={() => setMobileOpen((v) => !v)}
                className="md:hidden p-2 text-slate-700">
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-1 h-11 -mt-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-bold uppercase tracking-wide rounded-full transition ${
                    l.accent
                      ? "text-brand-green hover:bg-brand-greenLight"
                      : isActive
                        ? "bg-brand-navy text-white"
                        : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 px-4 py-4 space-y-3">
            <form onSubmit={submitSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="mobile-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-10 pr-4 h-10 rounded-full border border-slate-200 bg-slate-50 text-sm"
              />
            </form>
            <div className="grid grid-cols-2 gap-1">
              {NAV_LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className={`px-3 py-2.5 rounded-lg text-sm font-bold ${l.accent ? "text-brand-green bg-brand-greenLight" : "text-slate-800 bg-slate-50"}`}>
                  {l.label}
                </Link>
              ))}
            </div>
            {!user ? (
              <button onClick={login} data-testid="mobile-sign-in-btn"
                className="w-full bg-brand-navy text-white font-bold rounded-full py-2.5">
                Sign in with Google
              </button>
            ) : (
              <div className="pt-1 border-t border-slate-200 space-y-2" data-testid="mobile-account-block">
                <div className="flex items-center gap-2 px-1 pt-2 text-sm font-bold text-slate-800">
                  <User className="h-4 w-4 text-slate-500" />
                  {user.name || user.email || "Account"}
                </div>
                <Link to="/account" onClick={() => setMobileOpen(false)} data-testid="mobile-account-link"
                  className="block w-full text-center bg-slate-50 text-slate-800 font-bold rounded-full py-2.5">
                  Account
                </Link>
                <button onClick={() => { setMobileOpen(false); logout(); }} data-testid="mobile-sign-out-btn"
                  className="w-full bg-brand-red text-white font-bold rounded-full py-2.5 hover:bg-red-700 transition">
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </header>
    </>
  );
};
